/* ===========================
   analytics.js (no import.meta)
   =========================== */

/** GA Measurement ID
 *  Order of precedence:
 *  1) window.__GA_MEASUREMENT_ID__  (optional global override you can set in index.html)
 *  2) process.env.REACT_APP_GA_MEASUREMENT_ID (CRA/webpack)
 *  3) hardcoded fallback
 */
export const GA_ID =
  (typeof window !== 'undefined' && window.__GA_MEASUREMENT_ID__) ||
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GA_MEASUREMENT_ID) ||
  'G-0B7JJPJ9YP';

/** Basic dev detection (for debug_mode) */
const IS_DEV =
  (typeof window !== 'undefined' &&
    (/^localhost$|^127\.0\.0\.1$|^0\.0\.0\.0$/.test(window.location.hostname))) ||
  (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production');

/** Safe wrapper around gtag */
export function gaEvent(name, params = {}) {
  const w = typeof window !== 'undefined' ? window : undefined;
  if (w && typeof w.gtag === 'function') {
    const debug = IS_DEV ? { debug_mode: true } : {};
    w.gtag('event', name, { ...params, ...debug });
  }
}

/** Manual page_view for SPAs */
export function trackPageView(path) {
  const w = typeof window !== 'undefined' ? window : undefined;
  if (w && typeof w.gtag === 'function') {
    w.gtag('event', 'page_view', { page_path: path });
  }
}

/* ---------- Auto tracking: clicks, downloads, outbound ---------- */

const DOWNLOAD_EXT = ['pdf', 'zip', 'exe', 'dmg', 'msi', 'apk', 'tar', 'gz', '7z'];

function isDownloadLink(a) {
  const url = a.getAttribute('href') || '';
  const clean = url.split('?')[0].split('#')[0];
  const ext = clean.includes('.') ? clean.split('.').pop().toLowerCase() : '';
  return !!ext && DOWNLOAD_EXT.includes(ext);
}

function isOutboundLink(a) {
  try {
    const to = new URL(a.href, location.href);
    return to.hostname && to.hostname !== location.hostname;
  } catch {
    return false;
  }
}

/** Attach one global listener to capture relevant clicks */
export function initAutoClickTracking() {
  if (typeof document === 'undefined') return;

  const handler = (e) => {
    const target = /** @type {HTMLElement|null} */ (e.target);
    if (!target) return;

    // Any clickable button/anchor or element with data-analytics-id
    const clickable = target.closest('button, a, [data-analytics-id]');
    if (!clickable) return;

    // Which "window" did this click happen in?
    const within = clickable.closest('[data-window]');
    const window_name = within?.getAttribute('data-window') || undefined;

    // Derive a stable name for the control
    const button_name =
      clickable.getAttribute?.('data-analytics-id') ||
      clickable.getAttribute?.('id') ||
      clickable.getAttribute?.('aria-label') ||
      (clickable.textContent || '').trim().slice(0, 80) ||
      'unnamed';

    // Generic click event
    gaEvent('button_click', { button_name, window_name });

    // Special handling for <a> links
    if (clickable instanceof HTMLAnchorElement) {
      const href = clickable.getAttribute('href') || '';

      if (isDownloadLink(clickable)) {
        gaEvent('file_download', {
          file_name: href.split('/').pop(),
          file_extension: href.split('.').pop()?.toLowerCase(),
          window_name,
        });
      }

      if (isOutboundLink(clickable)) {
        let domain;
        try {
          domain = new URL(href, location.href).hostname;
        } catch {
          domain = undefined;
        }
        gaEvent('outbound_click', {
          link_url: href,
          link_domain: domain,
          window_name,
        });
      }
    }
  };

  // capture=true makes us see clicks before they’re stopped by other handlers
  document.addEventListener('click', handler, { capture: true });
}

/* ---------- Window lifecycle helpers ---------- */

export function trackWindowOpen(name) {
  gaEvent('window_open', { window_name: name });
}

export function trackWindowClose(name) {
  gaEvent('window_close', { window_name: name });
}

export function trackWindowFocus(name) {
  gaEvent('window_focus', { window_name: name });
}

export function trackWindowBlur(name) {
  gaEvent('window_blur', { window_name: name });
}

/* ---------- Engagement timing per window ---------- */

const activeTimers = new Map(); // window_name -> startTime (ms, performance.now)

export function startWindowEngagement(name) {
  if (activeTimers.has(name)) return;
  activeTimers.set(name, performance.now());
}

export function stopWindowEngagement(name) {
  const start = activeTimers.get(name);
  if (!start && start !== 0) return;
  activeTimers.delete(name);
  const duration_ms = Math.round(performance.now() - start);
  gaEvent('window_engagement', { window_name: name, engagement_ms: duration_ms });
}

/* ---------- Page visibility (session-level engagement) ---------- */

let pageVisibleAt = null;

export function initPageVisibilityTracking() {
  if (typeof document === 'undefined') return;

  const onVis = () => {
    if (document.visibilityState === 'visible') {
      pageVisibleAt = performance.now();
      gaEvent('page_focus', {});
    } else {
      if (pageVisibleAt != null) {
        const ms = Math.round(performance.now() - pageVisibleAt);
        pageVisibleAt = null;
        gaEvent('page_engagement', { engagement_ms: ms });
      }
      gaEvent('page_blur', {});
    }
  };

  document.addEventListener('visibilitychange', onVis);
  onVis(); // initialize immediately
}

/* ---------- Error tracking ---------- */

export function initErrorTracking() {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (e) => {
    gaEvent('js_error', {
      message: e.message,
      source: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason =
      (e && e.reason && (e.reason.message || String(e.reason))) || 'unknown';
    gaEvent('js_unhandledrejection', { reason });
  });
}
